import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ReviewDivider,
  ReviewDoubleRow,
  ReviewItem,
  ReviewItemLabel,
  ReviewItemValue,
  ReviewSection,
  ReviewSectionTitle
} from '../../../components/WizardV2/wizardStylesV2';
import { GET_BOOKING_COST_BREAKDOWN, GET_VOPAY_USER_PAYMENT_METHODS } from '../../../redux/constants';
import { setToast } from '../../../redux/actions/window';
import { requestFail, requestPending, requestSuccess } from '../../../utils/status';
import BookingPaymentBreakdownV2 from './BookingPaymentBreakdownV2';
import BookingDefaultCreditCardV2 from './BookingDefaultCreditCardV2';
import { useBookingCostBreakdownFetcher } from './useBookingCostBreakdownFetcher';
import { useBookingDefaultCreditCardFetcher } from './useBookingDefaultCreditCardFetcher';
import { resolveDefaultCreditCardState } from './bookingConfirmationhelper';

function isUnsetSlotTime(v) {
  return v == null || v === '' || v === -1 || v === '-1';
}

function AddBookingConfirmationStepV2({ t, getValues, amenity }) {
  const dispatch = useDispatch();
  const { bookingCostBreakdown, status: bookingStatus } = useSelector(
    (state) => state.parAmBooking
  );
  const { userPaymentMethods, userPaymentMethodsStatus } = useSelector(
    (state) => state.addPayment
  );

  const requirePayment = !!amenity?.require_payment;
  const buildingId = getValues('building')?.value;
  const amenityId = getValues('amenity_id');
  const startDateIso = getValues('startTime');
  const endDateIso = getValues('endTime');
  const residentUserId = getValues('user')?.value;

  const handleCostBreakdownFail = useCallback(() => {
    dispatch(
      setToast({
        toastShow: true,
        toastMessage: t('Failed to load booking cost!', {
          defaultValue: 'Failed to load booking cost!'
        }),
        modal: 'errorToast'
      })
    );
  }, [dispatch, t]);

  const handlePaymentMethodsFail = useCallback(() => {
    dispatch(
      setToast({
        toastShow: true,
        toastMessage: t('Failed to load payment method!', {
          defaultValue: 'Failed to load payment method!'
        }),
        modal: 'errorToast'
      })
    );
  }, [dispatch, t]);

  useBookingCostBreakdownFetcher({
    active: true,
    requirePayment,
    buildingId,
    amenityId,
    startDateIso,
    endDateIso,
    dispatch,
    onFail: handleCostBreakdownFail
  });

  useBookingDefaultCreditCardFetcher({
    active: true,
    requirePayment,
    userId: residentUserId,
    dispatch,
    onFail: handlePaymentMethodsFail
  });

  const costBreakdownLoading = bookingStatus === requestPending(GET_BOOKING_COST_BREAKDOWN);
  const costBreakdownFailed = bookingStatus === requestFail(GET_BOOKING_COST_BREAKDOWN);
  const paymentMethodLoading =
    requirePayment && userPaymentMethodsStatus === requestPending(GET_VOPAY_USER_PAYMENT_METHODS);
  const paymentMethodsLoaded =
    userPaymentMethodsStatus === requestSuccess(GET_VOPAY_USER_PAYMENT_METHODS) ||
    userPaymentMethodsStatus === requestFail(GET_VOPAY_USER_PAYMENT_METHODS);

  const defaultCreditCardState = useMemo(
    () =>
      resolveDefaultCreditCardState(userPaymentMethods, {
        loading: paymentMethodLoading,
        loaded: paymentMethodsLoaded
      }),
    [userPaymentMethods, paymentMethodLoading, paymentMethodsLoaded]
  );

  const formatDateFromPayloadInstant = (v) => {
    if (isUnsetSlotTime(v)) return '—';
    const m = moment.parseZone(v);
    if (!m.isValid()) return '—';
    return m.format('MMMM D, YYYY');
  };

  const formatTime = (v) => {
    if (isUnsetSlotTime(v)) return '—';
    const m = moment.parseZone(v);
    if (!m.isValid()) return '—';
    return m.format('h:mm a');
  };

  const formAmenityId = parseInt(getValues('amenity_id'), 10);
  const loadedAmenityId = parseInt(amenity?.id, 10);
  const amenityMatchesForm =
    Number.isFinite(formAmenityId) &&
    Number.isFinite(loadedAmenityId) &&
    formAmenityId === loadedAmenityId;
  const amenityName = amenityMatchesForm
    ? amenity?.description ?? amenity?.code ?? '—'
    : '—';

  const summaryUnit = () => {
    const user = getValues('user');
    return user?.unit_num ?? user?.unit?.unit_num ?? user?.unit_number ?? '—';
  };

  return (
    <>
      <ReviewDivider />
      <ReviewSection>
        <ReviewSectionTitle>
          {t('Booking Details', { defaultValue: 'Booking Details' })}
        </ReviewSectionTitle>
        <ReviewDoubleRow>
          <ReviewItem>
            <ReviewItemLabel>{t('Amenity Name', { defaultValue: 'Amenity Name' })}</ReviewItemLabel>
            <ReviewItemValue>{amenityName}</ReviewItemValue>
          </ReviewItem>
          <ReviewItem>
            <ReviewItemLabel>{t('Building')}</ReviewItemLabel>
            <ReviewItemValue>{getValues('building')?.label ?? '—'}</ReviewItemValue>
          </ReviewItem>
        </ReviewDoubleRow>
        <ReviewDoubleRow>
          <ReviewItem>
            <ReviewItemLabel>{t('Resident')}</ReviewItemLabel>
            <ReviewItemValue>{getValues('user')?.label ?? '—'}</ReviewItemValue>
          </ReviewItem>
          <ReviewItem>
            <ReviewItemLabel>{t('Unit')}</ReviewItemLabel>
            <ReviewItemValue>{summaryUnit()}</ReviewItemValue>
          </ReviewItem>
        </ReviewDoubleRow>
        <ReviewDoubleRow>
          <ReviewItem>
            <ReviewItemLabel>{t('Check-in', { defaultValue: 'Check-in' })}</ReviewItemLabel>
            <ReviewItemValue>{formatDateFromPayloadInstant(getValues('startTime'))}</ReviewItemValue>
          </ReviewItem>
          <ReviewItem>
            <ReviewItemLabel>{t('Check-out', { defaultValue: 'Check-out' })}</ReviewItemLabel>
            <ReviewItemValue>
              {formatDateFromPayloadInstant(getValues('endTime'))}
            </ReviewItemValue>
          </ReviewItem>
        </ReviewDoubleRow>
        <ReviewDoubleRow>
          <ReviewItem>
            <ReviewItemLabel>{t('Check-In Time', { defaultValue: 'Check-In Time' })}</ReviewItemLabel>
            <ReviewItemValue>{formatTime(getValues('startTime'))}</ReviewItemValue>
          </ReviewItem>
          <ReviewItem>
            <ReviewItemLabel>{t('Check-out Time', { defaultValue: 'Check-out Time' })}</ReviewItemLabel>
            <ReviewItemValue>{formatTime(getValues('endTime'))}</ReviewItemValue>
          </ReviewItem>
        </ReviewDoubleRow>
      </ReviewSection>

      {requirePayment ? (
        <BookingPaymentBreakdownV2
          t={t}
          breakdown={bookingCostBreakdown}
          loading={costBreakdownLoading}
          error={costBreakdownFailed ? true : null}
        />
      ) : null}

      {requirePayment ? (
        <BookingDefaultCreditCardV2
          t={t}
          display={defaultCreditCardState.display}
          loading={paymentMethodLoading}
          showExpiredError={defaultCreditCardState.showExpiredError}
        />
      ) : null}
    </>
  );
}

export default AddBookingConfirmationStepV2;
